import {
  Box,
  ClickAwayListener,
  Link,
  Paper,
  Popper,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { RecentFilterMode, RecentJobsPanelProps } from './RecentJobsPanel.props';

const FILTER_OPTIONS: { value: RecentFilterMode; label: string }[] = [
  { value: 'accessed', label: 'Accessed' },
  { value: 'modified', label: 'Modified' },
  { value: 'created', label: 'Created' },
];

export const RecentJobsPanel: React.FC<RecentJobsPanelProps> = ({
  anchorEl,
  open,
  onClose,
  jobs = [],
  filter = 'accessed',
  onFilterChange,
  canViewCreated = true,
}) => {
  const theme = useTheme();

  const handleFilterChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, newFilter: RecentFilterMode | null) => {
      if (newFilter !== null) onFilterChange?.(newFilter);
    },
    [onFilterChange]
  );

  const filterOptions = canViewCreated
    ? FILTER_OPTIONS
    : FILTER_OPTIONS.filter((opt) => opt.value !== 'created');

  // Guard against a 'created' selection when that option isn't offered (e.g.
  // permission revoked mid-session): without a matching button the toggle group
  // would render with nothing selected.
  const effectiveFilter = !canViewCreated && filter === 'created' ? 'accessed' : filter;

  if (!open || !anchorEl) return null;

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      data-testid="recent-jobs-panel"
      sx={{ zIndex: theme.zIndex.modal }}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Paper
          elevation={4}
          sx={{
            width: 360,
            maxHeight: 600,
            display: 'flex',
            flexDirection: 'column',
            border: `1px solid ${theme.palette.v4.border}`,
            borderRadius: 1,
          }}
        >
          <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.v4.border}` }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Recent Jobs
            </Typography>
            <ToggleButtonGroup
              value={effectiveFilter}
              exclusive
              onChange={handleFilterChange}
              size="small"
              aria-label="Filter recent jobs"
              sx={{ width: '100%' }}
            >
              {filterOptions.map((opt) => (
                <ToggleButton
                  key={opt.value}
                  value={opt.value}
                  aria-label={`Filter by ${opt.label}`}
                  sx={{
                    flex: 1,
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    py: 0.5,
                    border: `1px solid ${theme.palette.v4.border}`,
                    '&.Mui-selected': {
                      bgcolor: theme.palette.v4.accent,
                      color: theme.palette.v4.surface,
                      '&:hover': { bgcolor: theme.palette.v4.accent },
                    },
                  }}
                >
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <Box sx={{ overflowY: 'auto', flex: 1 }}>
            {jobs.length === 0 ? (
              <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  No recent jobs
                </Typography>
              </Box>
            ) : (
              jobs.map((job) => (
                <Box
                  key={job.id}
                  sx={{
                    px: 2,
                    py: 1.25,
                    borderBottom: `1px solid ${theme.palette.v4.border}`,
                    '&:hover': { bgcolor: theme.palette.v4.tableHover },
                    cursor: 'pointer',
                  }}
                >
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <Link
                      component={RouterLink}
                      to={`/services/${job.id}`}
                      underline="hover"
                      aria-label={`Open job ${job.jobId}`}
                      sx={{ color: theme.palette.v4.accent, fontWeight: 500, fontSize: '0.875rem' }}
                    >
                      {job.jobId}
                    </Link>
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                    >
                      {job.time}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.primary', fontSize: '0.8125rem', mt: 0.25 }}
                  >
                    {job.client} &mdash; {job.location}
                  </Typography>
                </Box>
              ))
            )}
          </Box>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};
